// src/lib/settings/useSettings.tsx
//
// Application settings store. React Context + useReducer +
// localStorage under 'bench-settings-v1'. Schema-guarded loader.
//
// Public:
//   useSettings() -> { entries, get, set, reset }
//   getSettings(category) -> SettingsEntry[]
//
// Test helper:
//   _resetSettingsStorage()

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import type { AuditEntry } from "@/lib/demoData";
import {
  type SettingsCategory,
  type SettingsEntry,
  SEED_SETTINGS,
} from "./types";

const STORAGE_KEY = "bench-settings-v1";
const AUDIT_STORAGE_KEY = "bench-settings-audit-v1";

// ---------- storage ----------

function loadFromStorage(): SettingsEntry[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every(
        (e) => typeof e === "object" && e && "key" in e && "value" in e,
      )
    ) {
      return parsed as SettingsEntry[];
    }
    return null;
  } catch {
    return null;
  }
}

function saveToStorage(rows: SettingsEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // ignore quota / private-mode
  }
}

function loadAudit(): AuditEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(AUDIT_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AuditEntry[]) : [];
  } catch {
    return [];
  }
}

function saveAudit(rows: AuditEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(AUDIT_STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // ignore
  }
}

// ---------- reducer ----------

type Action =
  | { type: "replaceAll"; payload: SettingsEntry[] }
  | { type: "reset" }
  | {
      type: "set";
      payload: {
        key: string;
        value: string;
        actor: string;
      };
    };

function reducer(rows: SettingsEntry[], action: Action): SettingsEntry[] {
  switch (action.type) {
    case "replaceAll":
      return action.payload;
    case "reset":
      return SEED_SETTINGS.map((e) => {
        const { lastEditedAt: _a, lastEditedBy: _b, ...rest } = e;
        return rest;
      });
    case "set": {
      const now = new Date().toISOString();
      return rows.map((r) =>
        r.key === action.payload.key
          ? {
              ...r,
              value: action.payload.value,
              lastEditedAt: now,
              lastEditedBy: action.payload.actor,
            }
          : r,
      );
    }
  }
}

// ---------- context ----------

export interface SettingsContextValue {
  entries: SettingsEntry[];
  audit: AuditEntry[];
  get: (key: string) => SettingsEntry | undefined;
  byCategory: (category: SettingsCategory) => SettingsEntry[];
  set: (key: string, value: string, actor: string) => void;
  reset: () => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [rows, dispatch] = useReducer(
    reducer,
    undefined,
    () => loadFromStorage() ?? SEED_SETTINGS,
  );
  const [audit, setAudit] = useReducer(
    (s: AuditEntry[], a: { type: "add"; entry: AuditEntry }) => {
      const next = [a.entry, ...s].slice(0, 100);
      saveAudit(next);
      return next;
    },
    undefined,
    () => loadAudit(),
  );

  useEffect(() => {
    saveToStorage(rows);
  }, [rows]);

  const value = useMemo<SettingsContextValue>(
    () => ({
      entries: rows,
      audit,
      get: (k) => rows.find((r) => r.key === k),
      byCategory: (c) => rows.filter((r) => r.category === c),
      set: (key, value, actor) => {
        const entry = rows.find((r) => r.key === key);
        if (!entry || entry.value === value) return;
        const before = entry.value;
        const redacted = entry.sensitive ? "*** redacted ***" : value;
        setAudit({
          type: "add",
          entry: {
            id: `AUD-S-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            ts: new Date().toISOString(),
            actor,
            action: "settings-update",
            summary: `Settings '${key}': '${before}' -> '${redacted}'`,
          },
        });
        dispatch({ type: "set", payload: { key, value, actor } });
      },
      reset: () => {
        setAudit({
          type: "add",
          entry: {
            id: `AUD-S-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            ts: new Date().toISOString(),
            actor: "system",
            action: "settings-reset",
            summary: "Settings reset to seed defaults.",
          },
        });
        dispatch({ type: "reset" });
      },
    }),
    [rows, audit],
  );

  return (
    <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error("useSettings must be used inside a <SettingsProvider />");
  }
  return ctx;
}

export function _resetSettingsStorage(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(AUDIT_STORAGE_KEY);
  } catch {
    // ignore
  }
}
